<template>
  <div v-if="competitors_loading" class="w-full flex justify-center items-center">
    <div class="w-full text-center max-w-lg bg-white dark:bg-dark-box-background dark:text-white p-8 rounded-lg shadow-lg space-y-4">
      <!-- Spinner Icon -->
      <div class="flex justify-center items-center">
        <svg class="animate-spin h-10 w-10 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>

      <!-- Title and Message -->
      <h1 class="text-2xl font-semibold mt-4">
        {{ $t("Fetching Insights") }}
      </h1>
      <p class="text-gray-500 dark:text-gray-300">
        {{ $t("This might take a few moments. Hang tight while we gather your data.") }}
      </p>

      <!-- Progress Bar -->
      <div class="w-full bg-gray-200 rounded-full dark:bg-gray-700 h-2.5 mt-6">
        <div class="bg-blue-500 h-2.5 rounded-full" :style="{ width: `${progress}%` }" />
      </div>
    </div>
  </div>
  <div v-else class="relative flex flex-col w-full">
    <RecordTitleComponent class="-ml-5" :linked-record="linkedRecord" :connect-to="connectTo" />
    <div class="relative flex">
      <div v-if="!is_expanded">
        <span>
          {{ record_data?.description?.length > 200 ? record_data.description.slice(0, 200) + "..." : record_data.description }}
          <span
            v-if="!is_expanded && record_data?.description?.length > 200"
            class="text-blue-500 hover:underline flex flex-nowrap whitespace-nowrap w-fit hover:cursor-pointer"
            @click.prevent="is_expanded = !is_expanded">
            {{ $t("read more") }}
          </span>
        </span>
      </div>
      <div v-if="is_expanded">
        {{ record_data?.description }}
      </div>
    </div>
    <div class="flex flex-col mt-7">
      <span class="text-2xl font-medium">{{ $t("Company details") }}</span>
      <div class="relative flex flex-wrap items-center -mx-4">
        <div v-for="metric in metrics" :key="metric" class="w-1/4 px-4 py-2">
          <RollupSimple :metric="metric" class="" />
        </div>
        <div
          class="w-1/4 px-4 py-2 cursor-pointer"
          @click="
            $emitter.emit(ALL_EMITTERS.UPDATE_METRICS, {
              page_reference: pageReference,
              view_type: VIEW_TYPES.TABLE
            })
          ">
          <div class="relative flex items-center w-full h-full min-w-[160px] min-h-[70px] bg-[#f5f5f5] rounded-xl">
            <div class="flex items-center justify-center bg-white rounded-full mx-2 w-[48px] h-[48px]">
              <Plus class="h-6 w-6 text-[#899399]" aria-hidden="true" />
            </div>
            <span class="text-[#899399]">{{ $t("Add your own fields") }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col mt-7 overflow-y-scroll">
      <div class="relative flex flex-nowrap gap-x-2 w-full">
        <div class="w-1/3">
          <span class="text-2xl font-medium">{{ $t("Competitors") }}</span>
          <div
            v-for="competitor in $utils.orderBy(record_data?.competitors, 'score').reverse()"
            :key="competitor?.id"
            class="flex items-center space-x-2 w-full mt-4">
            <img v-if="competitor?.logo" class="inline-block rounded-full h-10 w-10 object-contain" :src="competitor.logo" alt="">
            <div class="flex flex-col -space-y-1">
              <div class="flex items-center text-lg font-medium">
                {{ competitor?.name }}
              </div>
              <a
                target="_blank"
                class="text-md text-common-grey font-normal capitalize dark:text-dark-brand-blue"
                :href="`//${competitor.domain_name}`">{{ competitor.domain_name }}
              </a>
            </div>
          </div>
        </div>
        <div class="w-1/3">
          <span class="text-2xl font-medium">{{ $t("News") }}</span>
          <div
            v-for="news_ in record_data?.news"
            :key="news_?.id"
            class="flex items-center cursor-pointer space-x-2 w-full mt-4"
            @click="openLink(news_?.link)">
            <img v-if="news_?.logo" class="inline-block h-10 w-10" :src="news_.logo" alt="">
            <div class="w-[calc(100%-50px)] max-w-[calc(100%-50px)] flex flex-col -space-y-1">
              <div
                class="truncate text-ellipsis relative w-full flex items-center text-lg font-medium flex-nowrap whitespace-nowrap max-w-full">
                {{ news_.headline }}
              </div>
              <div v-if="news_?.publish_date" class="text-md text-common-grey font-normal capitalize">
                {{ $moment(news_.publish_date).format("DD MMM YYYY") }}
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-col w-1/3">
          <div class="flex items-center">
            <span class="text-2xl font-medium">{{ $t("Key People") }}</span>
            <Menu v-if="component_record?.key_records?.length" as="div" class="flex-shrink-0">
              <MenuButton
                class="w-5 h-5 inline-flex items-center justify-center text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
                <EllipsisVertical class="w-5 h-5" aria-hidden="true" />
              </MenuButton>
              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95">
                <MenuItems
                  class="absolute z-50 mx-3 -ml-[160px] whitespace-nowrap origin-bottom-right dark:bg-dark-screen-background w-48 mt-1 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <MenuItem v-slot="{ active }" class="py-4">
                    <div
                      :class="[
                        active ? 'bg-gray-100 dark:bg-dark-screen-background text-gray-400' : 'text-gray-700 dark:text-dark-body-text',
                        ' cursor-pointer group flex items-center px-4 py-2 '
                      ]"
                      @click="
                        $emitter.emit(ALL_EMITTERS.EDIT_KEY_RECORDS, {
                          record: component_record
                        })
                      ">
                      <Pencil class="mr-3 h-5 w-5" /> {{ $t("Edit List") }}
                    </div>
                  </MenuItem>

                  <MenuItem v-slot="{ active }" class="py-4 cursor-pointer">
                    <div
                      :class="[
                        active ? 'bg-gray-100 dark:bg-dark-screen-background text-gray-400' : 'text-gray-700 dark:text-dark-body-text',
                        ' cursor-pointer group flex items-center px-4 py-2 '
                      ]"
                      @click="
                        $emitter.emit(ALL_EMITTERS.EMPTY_KEY_RECORDS, {
                          record: component_record
                        })
                      ">
                      <Trash2 class="mr-3 h-5 w-5" /> {{ $t("Empty List") }}
                    </div>
                  </MenuItem>
                </MenuItems>
              </transition>
            </Menu>
          </div>
          <div v-if="!component_record?.key_records?.length" class="flex flex-col items-center w-full mt-4">
            <Gem class="w-16 h-16 ml-1 cursor-pointer" color="#B3CAD6" />
            <p class="font-bold text-[20px] mt-2">
              {{ $t("You have no Key People in your list!") }}
            </p>
            <div
              class="flex items-center border-transparent bg-gradient-to-r from-[#2DA7D7] to-[#841FF4] px-6 mt-4 font-bold text-white rounded-full h-[50px] text-[14px] w-fit hover:cursor-pointer"
              @click="$emitter.emit(ALL_EMITTERS.EDIT_KEY_RECORDS, { record: component_record })">
              <Plus class="h-6 w-6 mr-2 text-white" aria-hidden="true" />
              <span>{{ $t("Add Key People") }}</span>
            </div>
          </div>
          <div
            v-for="key_record in component_record?.key_records"
            :key="key_record?.id"
            class="flex cursor-pointer space-x-2 w-full group/tab mt-4"
            @click="$utils.Record(key_record).open()">
            <Avatar
              :radius="40"
              class="mt-1 mr-1"
              :hover-effect="true"
              :first-name="key_record?.name?.split(' ')[0]"
              :last-name="key_record?.name?.split(' ')[1]" />
            <div class="flex flex-col -space-y-1">
              <div class="text-lg font-medium">
                {{ $utils.getRecordName(key_record) }}
              </div>
              <div class="text-md text-common-grey font-normal capitalize">
                {{ key_record.type }}
              </div>
              <div class="flex w-full dark:border-dark-divider-border rounded-lg h-full py-1">
                <div class="flex flex-grow w-full">
                  <div class="flex-1" />
                  <div class="flex justify-start w-full pl-0 cursor-pointer">
                    <span
                      v-for="i in 5"
                      :key="i"
                      @click.stop="
                        () => {
                          key_record.values[$store.state.integration.metrics_mapping.contact_relationship_strength_metric] = i
                          handleUpdateStarValue(i, key_record)
                        }
                      ">
                      <svg
                        class="block w-5 h-5 mr-2"
                        :class="[
                          key_record?.values
                            ? key_record?.values[$store.state.integration.metrics_mapping.contact_relationship_strength_metric] >= i
                              ? key_record?.values[$store.state.integration.metrics_mapping.contact_relationship_strength_metric] >= 4
                                ? 'text-status-success-green'
                                : key_record?.values[$store.state.integration.metrics_mapping.contact_relationship_strength_metric] >= 3
                                  ? 'text-status-warning-yellow'
                                  : 'text-status-error-red'
                              : 'text-gray-300'
                            : null
                        ]"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="!ml-auto mt-4 relative right-2 cursor-pointer" @click.stop="$emitter.emit('sendEmail', { record: key_record })">
              <Mail color="#2DA7D7" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useLayoutItemSharedLogic } from "@/components/shared/pages/useLayoutItemSharedLogic.js"
import dataService from "@/services/data.service"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue"
import RecordTitleComponent from "@/components/shared/RecordTitleComponent.vue"
import RollupSimple from "@/components/shared/rollup-metrics/RollupSimple.vue"
import Avatar from "@/components/shared/Avatar.vue"
import { Mail, Plus, Trash2, EllipsisVertical, Pencil, Gem } from "lucide-vue-next"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { VIEW_TYPES } from "@/store/constants/references"

export default {
  components: {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    EllipsisVertical,
    Pencil,
    Trash2,
    Mail,
    Avatar,
    RecordTitleComponent,
    RollupSimple,
    Gem,
    Plus
  },
  props: {
    linkedRecord: null,
    connectTo: null,
    pageReference: {
      type: String,
      required: true
    }
  },
  emits: [],
  data() {
    return {
      component_record: null,
      record_error: null,
      loading: false,
      progress: 0,
      competitors_loading: true,
      record_data: {
        name: "",
        description: "",
        linkedin: "",
        domain_name: "",
        founded_year: null,
        headquarter: "",
        industry: "",
        stats: {
          annual_revenue: 0,
          employees: 0
        },
        competitors: [{}],
        news: [{}]
      },
      is_expanded: false,
      metrics: [],
      ALL_EMITTERS,
      VIEW_TYPES,
      resources_loaded: false
    }
  },
  mounted() {
    // Simulate loading progress
    const interval = setInterval(() => {
      if (this.progress < 100) {
        this.progress += 1 // Adjust the increment speed as needed
      } else {
        clearInterval(interval)
      }
    }, 50)
  },
  created() {
    const { loading, record_error, component_record } = useLayoutItemSharedLogic(
      this.$props,
      this.postGetComponentRecord,
      this.registerListeners,
      this.unregisterListeners
    )
    this.loading = loading
    this.record_error = record_error
    this.component_record = component_record
  },
  methods: {
    registerListeners() {},
    unregisterListeners() {},
    postGetComponentRecord() {
      this.fetchCompanyInsights(this.component_record)
    },
    fetchCompanyInsights(company) {
      dataService.getRecordInsights(company?.id).then((res) => {
        if (res.data.status == "RECORD_INSIGHTS_GENERATION_INITIATED") {
          setTimeout(() => {
            this.fetchCompanyInsights(company)
          }, 5000)
        } else {
          this.resources_loaded = true
          let insights = res.data

          this.competitors_loading = false
          this.record_data = insights
          if (this.record_data?.linkedin && this.metrics.find((m) => m.input_type === "Linkedin") === undefined) {
            this.metrics.push({
              label: this.$t("Visit Profile"),
              input_type: "Linkedin",
              description: this.$t("LinkedIn"),
              link: this.record_data.linkedin
            })
          }
          if (this.record_data?.domain_name && this.metrics.find((m) => m.input_type === "Website") === undefined) {
            this.metrics.push({
              label: this.$t("Visit Page"),
              input_type: "Website",
              description: this.$t("Website"),
              link: this.record_data.domain_name
            })
          }
          if (this.record_data?.founded_year && this.metrics.find((m) => m.input_type === "Founded") === undefined) {
            this.metrics.push({
              label: this.record_data.founded_year,
              input_type: "Founded",
              description: this.$t("Founded")
            })
          }
          if (this.record_data?.headquarter && this.metrics.find((m) => m.input_type === "Adress") === undefined) {
            this.metrics.push({
              label: this.record_data.headquarter,
              input_type: "Adress",
              description: this.$t("Headquarters")
            })
          }
          if (this.record_data?.industry && this.metrics.find((m) => m.input_type === "Industry") === undefined) {
            this.metrics.push({
              label: this.record_data.industry,
              input_type: "Industry",
              description: this.$t("Industry")
            })
          }
          if (this.record_data?.stats?.employees && this.metrics.find((m) => m.input_type === "EmployeeCount") === undefined) {
            this.metrics.push({
              label: this.record_data.stats.employees,
              input_type: "EmployeeCount",
              description: this.$t("Employee Count")
            })
          }
          if (this.record_data?.stats?.annual_revenue && this.metrics.find((m) => m.input_type === "Currency") === undefined) {
            this.metrics.push({
              label: this.record_data.stats.annual_revenue,
              input_type: "Currency",
              description: this.$t("Annual Revenue")
            })
          }
        }
      })
    },
    openLink(link) {
      window.open(link, "_blank")
    },
    handleUpdateStarValue(i, key_record) {
      this.$store
        .dispatch("data/updateRecord", {
          record_id: key_record.id,
          data: {
            values: {
              ...key_record.values,
              [this.$store.state.integration.metrics_mapping.contact_relationship_strength_metric]: i
            }
          }
        })
        .then(() => {
          this.$emitter.emit(ALL_EMITTERS.UPDATE_KEY_RECORDS_TIED_RECORD)
        })
    }
  }
}
</script>
